import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { activitiesAPI } from "../services/api";

// Seitengrösse für serverseitige Pagination; teilbar durch 1, 2, 3, 4 für
// volle Reihen im Grid.
export const PAGE_SIZE = 12;

// Obergrenze der über «Mehr laden» erreichbaren Aktivitäten pro Suche.
// Wer mehr sehen will, soll die Filter (Datum, Distanz, Kategorie …)
// verfeinern, statt endlos nachzuladen. Vielfaches von PAGE_SIZE.
export const MAX_ACTIVITIES = 120;

// Der Server liefert pro Zeitslot eines Events einen eigenen Eintrag
// (z.B. ein wiederkehrender Event mit mehreren Uhrzeiten am selben Tag).
// Für die Kartenansicht sollen diese zu einer einzigen Aktivität mit
// einer Liste aller Uhrzeiten (`active_times`) zusammengefasst werden.
const groupActivitiesByTimeSlots = (list) => {
  const order = [];
  const byId = new Map();

  for (const activity of list) {
    const existing = byId.get(activity.id);
    if (!existing) {
      byId.set(activity.id, {
        ...activity,
        active_times: activity.active_time ? [activity.active_time] : [],
      });
      order.push(activity.id);
    } else if (activity.active_time) {
      existing.active_times.push(activity.active_time);
    }
  }

  return order.map((id) => byId.get(id));
};

export const useActivities = (filters) => {
  // Rohliste vom Server (ein Eintrag pro Zeitslot); Karten-Gruppierung s.u.
  const [rawItems, setRawItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    const fetchActivities = async () => {
      const isInitialLoad = isInitialLoadRef.current;
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setIsFetching(true);
      }
      try {
        // Nach Rückkehr von der Detailseite so viele Aktivitäten laden wie
        // zuvor sichtbar waren, damit die Scroll-Position wiederherstellbar
        // ist (Schlüssel wird von GridView nach dem Restore entfernt).
        const savedCount = parseInt(
          sessionStorage.getItem("gridVisibleCount") || "0",
          10
        );
        const limit =
          isInitialLoad && savedCount > PAGE_SIZE
            ? Math.min(savedCount, MAX_ACTIVITIES)
            : PAGE_SIZE;
        const response = await activitiesAPI.getAll(filters, {
          limit,
          offset: 0,
        });
        setRawItems(response.data.items);
        setTotal(response.data.total);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        if (isInitialLoad) {
          setLoading(false);
          isInitialLoadRef.current = false;
        }
        setIsFetching(false);
      }
    };

    const debounceId = setTimeout(
      fetchActivities,
      isInitialLoadRef.current ? 0 : 300
    );
    return () => clearTimeout(debounceId);
  }, [filters]);

  const activities = useMemo(
    () => groupActivitiesByTimeSlots(rawItems),
    [rawItems]
  );

  // «Mehr laden»: nächste Seite vom Server anhängen. Der Server paginiert
  // auf Aktivitätsebene, daher entspricht der Offset der Anzahl bereits
  // geladener Karten (nicht der Zeitslot-Einträge).
  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    const limit = Math.min(PAGE_SIZE, MAX_ACTIVITIES - activities.length);
    if (limit <= 0) return;
    setLoadingMore(true);
    try {
      const response = await activitiesAPI.getAll(filters, {
        limit,
        offset: activities.length,
      });
      setRawItems((prev) => [...prev, ...response.data.items]);
      setTotal(response.data.total);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [filters, activities.length, loadingMore]);

  return {
    activities,
    total,
    loading,
    isFetching,
    loadingMore,
    error,
    loadMore,
  };
};
